import { Grid, GridSize } from "@material-ui/core";
import { ReactComponent as CenterDotIcon } from "assets/CenterDotIcon.svg";
import useComments from "backend/hooks/comments/useComments";
import useAttachments from "backend/hooks/files/useAttachments";
import { ICardElementWithChildren } from "backend/model/model";
import { t } from "i18next";
import RichTextViewer from "qpr-base/ui/components/RichTextViewer";
import UploadedFilesList from "qpr-base/ui/components/UploadedFilesList";
import React from "react";
import { useAppContext } from "../../qpr-base/context/AppCtxProvider";
import { makeThemedStyles } from "../../qpr-base/viewcomponents/utils";
import InputContainer from "../../design/components/forms/InputContainer";
import TextInput from "../../design/components/forms/TextInput";

interface UpdateProps {
  value: string;
  id?: string;
  name?: string;
}
interface Props {
  element: ICardElementWithChildren;
  isDisabled: boolean;
  handleField: (val: UpdateProps) => any;
}
const useStyles = (rtl: boolean) =>
  makeThemedStyles("", (theme) => ({
    cellContent: { display: "flex", flexWrap: "nowrap", padding: "5px" },
    heading: {
      background: "none",
      textAlign: "start",
      fontFamily: "Arial",
      fontSize: "12px",
      display: "flex",
      flewWrap: "nowrap",
      fontWeight: 400,
      lineHeight: "1.25em",
      letterSpacing: "0em",
      color: "#374151",
      margin: 0,
      marginTop: "8px",
      marginInlineStart: "22px",
    },
    centerdot: {
      marginTop: "-2px",
    },
    unitSymbol: {
      fontSize: "14px",
      marginTop: "11px",
      marginLeft: "8px",
    },
    attachments: {
      margin: "5px 0 14px 20px",
    },
  }));

const SubElementContents = (props: Props) => {
  const ctx = useAppContext();
  const classes = useStyles(ctx.rtl)();
  const { handleField, isDisabled, element } = props;
  const id = element.id;
  const period = element.periodname || "";

  const { data: comments, isLoading: isLoadingComments } = useComments({
    id,
    period,
  });
  const { data: attachments, isLoading: isLoadingAttachments } = useAttachments(
    { elementId: id, period }
  );

  const handleChange = (value: string, series: string) => {
    // REMOVED regex that was blocking dates (e.g., 2023-01-01)
    handleField({ value: value, name: series, id: element.id });
  };



  const pad2 = (n: number) => String(n).padStart(2, "0");

  const formatDateForInput = (val: string) => {
    if (!val) return "";

    // If it's already YYYY-MM-DD, return it
    if (/^\d{4}-\d{2}-\d{2}$/.test(val)) return val;

    // Handle ISO date-time strings explicitly (keeps the calendar date as written)
    // Examples: 2025-11-24T10:30:00Z, 2025-11-24T10:30:00+02:00
    const isoMatch = val.match(/^(\d{4})-(\d{2})-(\d{2})T/);
    if (isoMatch) {
      const [, y, m, d] = isoMatch;
      return `${y}/${m}/${d}`;
    }

    // Fallback: parse with Date, but format using local time components
    const d = new Date(val);
    if (!isNaN(d.getTime())) {
      const y = d.getFullYear();
      const m = pad2(d.getMonth() + 1);
      const day = pad2(d.getDate());
      return `${y}-${m}-${day}`; // local date, no UTC shift
    }

    // If not parseable, return as-is
    return val;
  };

  const getSubElementFields = () => {
    const visibleSeries =
      element.typename === "Milestone"
        ? [
            {
              series: "END_DATE",
              attribute: "prettyvalue",
              inputType: "date",
            },
            {
              series: "COMPLETION_PERCENTAGE",
              attribute: "valuewithoutsymbol",
              inputType: "text",
            },
            {
              series: "EXPENDITURE",
              attribute: "valuewithoutsymbol",
              inputType: "text",
            },
          ]
        : [
            {
              series: "ACT",
              attribute: "valuewithoutsymbol",
              inputType: "text",
            },
            {
              series: "TAR",
              attribute: "valuewithoutsymbol",
              inputType: "text",
            },
          ];
    const spacePerCell: number = Math.floor(
      12 / Math.min(visibleSeries.length, 3)
    );

    const getSeriesValue = (
      series: string,
      attribute: string,
      type: string
    ) => {
      let val = (element as any)[`${series}_${attribute}`];

      val = val === undefined || val === null ? "" : String(val);

      if (type === "date") {
        console.log("Formatting date value:", val);
        return formatDateForInput(val);
      }
      return val;
    };

    return visibleSeries.map((ser: any) => (
      <Grid item xs={spacePerCell as GridSize} key={ser.series}>
        <div className={classes.cellContent}>
          <InputContainer title={t(`configtypes.${ser.series}`)} width="100%">
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                width: "100%",
              }}
            >
              <TextInput
                disabled={isDisabled}
                type={ser.inputType}
                fullWidth={true}
                value={getSeriesValue(ser.series, ser.attribute, ser.inputType)}
                onChange={(val) => handleChange(val, ser.series)}
              />
              <div className={classes.unitSymbol}>
                {t(getSeriesValue(ser.series, "unit", "text"))}
              </div>
            </div>
          </InputContainer>
        </div>
      </Grid>
    ));
  };

  if (isLoadingAttachments || isLoadingComments) return null;

  return (
    <>
      <Grid container spacing={0}>
        {getSubElementFields()}
      </Grid>

      {attachments?.length || comments?.length ? (
        <div className={classes.heading}>
          {t("kpis.Actions and Recommendations")}
          <div className={classes.centerdot}>
            <CenterDotIcon />
          </div>
          <div>{comments?.length ? comments[0].modifiedby : ""}</div>
        </div>
      ) : null}

      {comments?.length ? (
        <div>
          <RichTextViewer
            value={comments?.length ? comments?.[0].description : ""}
          />
        </div>
      ) : null}

      {attachments?.length ? (
        <>
          <div className={classes.attachments}>
            <UploadedFilesList values={attachments} readOnly />
          </div>
        </>
      ) : null}
    </>
  );
};
export default SubElementContents;
